import { colorsMap } from 'constants/cardAssetsList';
import { Assets, CanvasSource, Container, Sprite, Texture } from 'pixi.js';
import { Store } from './store/gameStore';
import { EventName } from './events/eventsName';
import { GameEvent } from './events/gameEvent';
import { DesignTabs } from './gameUI/DesignSection';
import { Displays } from 'constants/layout';
import { Layout } from './LayoutManager';
let supportWebP = true;

const firstLoad = [
    'placeholders/slot-ace', //
    'placeholders/slot-empty',
    'placeholders/slot-load',
    'placeholders/slot-empty-no-cards',
    'placeholders/slot-empty-restart',
    'cards/shadow',

    //here will be added cards
];

const uiToLoad = [
    'settings/card-fronts.svg',
    'settings/card-bgs.svg',
    'settings/card-backs.svg',
    'settings/itsOn',
    'settings/itsOff',
    'particles/clubs_face',
    'particles/spades_face',
    'particles/hearts_face',
    'particles/diamonds_face',
    'settings/feedback.svg',
    'kingPopup/king',
    'kingPopup/cloud',
    'buttons/close.svg',
    'buttons/undo.svg',
    'buttons/settings.svg',
    'buttons/design.svg',
    'buttons/rules.svg',
    'buttons/dots.svg',
    'buttons/hint.svg',
    'buttons/wand.svg',
    'hints/glowTop',
    'MagicWand/magicWand',
];

let gameAssets = {} as { [key: string]: Texture };

function getFrontsPaths() {
    const pathToLoad = [] as string[];
    colorsMap.forEach((color) => {
        for (let num = 0; num < 13; num++) {
            let cardValueNumber = num + 1;
            let cardName = '';
            const frontTheme = Store.front;
            switch (cardValueNumber) {
                case 10:
                    cardName = `10-${color}`;
                    break;
                case 11:
                    cardName = `j-${color}`;
                    break;
                case 12:
                    cardName = `q-${color}`;
                    break;

                case 13:
                    cardName = `k-${color}`;
                    break;
                case 1:
                    cardName = `ace-${color}`;
                    break;
                default:
                    cardName = `${cardValueNumber}-${color}`;
                    break;
            }
            pathToLoad.push(`cards/front/${frontTheme}/${cardName}.${webpFallback('png')}`);
        }
    });
    return pathToLoad;
}

enum AssetStatus {
    PENDING = 'pending',
    IDLE = 'idle',
    LOADED = 'loaded',
    ERROR = 'error',
}
type AssetItem = {
    alias: string;
    src: string;
    status: AssetStatus;
};
const beforeGameManifest = [] as AssetItem[];
const afterGameManifest = [] as AssetItem[];

function createManifest() {
    firstLoad.forEach((src) => {
        addToBundle(beforeGameManifest, src, webpFallback('png'));
    });

    getFrontsPaths().forEach((src) => {
        addToBundle(beforeGameManifest, src, webpFallback('png'));
    });

    addToBundle(beforeGameManifest, 'cards/back/' + Store.back);
    uiToLoad.forEach((src) => {
        addToBundle(afterGameManifest, src, webpFallback('png'));
    });
}
function addToBundle(array: AssetItem[], src: string, ext = 'webp') {
    const parts = src.split('.');
    if (parts.length === 2) {
        ext = parts[1];
        src = parts[0];
    }
    array.push({
        alias: `${src}.${ext}`,
        src: `${window.location.origin}/assets/${src}.${ext}`,
        status: AssetStatus.IDLE,
    });
}
export async function preLoadGameAssets(preloadCb: () => void) {
    const assets = [] as AssetItem[];

    let folder = 'desktop';
    switch (Layout.getDisplay()) {
        case Displays.MOBILE:
            folder = 'mobile';
            break;
        case Displays.TABLET:
            folder = 'tablet';
            break;
        default:
            folder = 'desktop';
            break;
    }
    addToBundle(assets, `backgrounds/${folder}/${Store.background}`, webpFallback('jpg'));

    addToBundle(assets, 'loader/loader.svg');
    await loadAssets(assets);
    preloadCb();
}
export async function loadMainAssets(preloadCb: () => void) {
    createManifest();
    await loadAssets(beforeGameManifest);
    preloadCb();
}
async function loadAssets(assets: AssetItem[]) {
    const promises = assets.map((asset) => {
        return new Promise<void>(async (resolve, reject) => {
            asset.status = AssetStatus.PENDING;

            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = asset.src;

            img.onload = () => {
                try {
                    // await img.decode();
                    asset.status = AssetStatus.LOADED;
                    if (asset.alias) {
                        const texture = convertImgToCanvasSource(img);
                        gameAssets[asset.alias] = texture;
                        resolve();
                    }
                } catch (err) {
                    asset.status = AssetStatus.ERROR;
                    reject(err);
                }
            };

            img.onerror = () => {
                asset.status = AssetStatus.ERROR;
                reject(new Error(`Failed to load image: ${asset.src}`));
            };
        });
    });

    await Promise.allSettled(promises);
}
export async function lateAssetLoad() {
    await loadAssets(afterGameManifest);
    onAssetsLoadComplete();
}

export async function preloadDesignAssets(type: DesignTabs, cbEvent: EventName) {
    const designToLoad = [] as AssetItem[];

    switch (type) {
        case DesignTabs.BACKS:
            GameEvent.emit(EventName.SHOW_LOADER);
            addToBundle(designToLoad, `cards/back/${Store.back}`, webpFallback('png'));
            Assets.add({
                alias: `cards/back/${Store.back}.${webpFallback('png')}`,
                src: `${window.location.origin}/assets/cards/back/${Store.back}.${webpFallback('png')}`,
            });
            break;

        case DesignTabs.FRONTS:
            GameEvent.emit(EventName.SHOW_LOADER);
            const newFront = getFrontsPaths();
            newFront.forEach((front, index) => {
                addToBundle(designToLoad, front, webpFallback('png'));
            });
    }
    await loadAssets(designToLoad);

    GameEvent.emit(EventName.HIDE_LOADER);
    GameEvent.emit(cbEvent);
    GameEvent.emit(EventName.DESIGN_ASSET_SET);
}
export async function getAsset(path: string, extensions = webpFallback('png')): Promise<Texture> {
    let ext = '';
    let extList: string[] = [];

    if (extensions) {
        extList = extensions.split('.');

        if (extList.includes('webp')) {
            ext = 'webp';
        } else {
            ext = extList[0];
        }
        ext = `.${ext}`;
    }

    const imgSrc = path + ext;
    return new Promise((resolve) => {
        if (gameAssets[imgSrc]) {
            resolve(gameAssets[imgSrc]);
            return;
        }

        const img = new Image();
        img.src = `${window.location.origin}/assets/${imgSrc}`;

        img.onload = async () => {
            await img.decode(); // Waits until image is completely ready

            const texture = convertImgToCanvasSource(img);
            gameAssets[img.src] = texture;
            resolve(texture);
        };
        img.onerror = () => {
            console.error(`Failed to load image: ${img.src}`);
            resolve(Texture.EMPTY);
        };
    });
}
export async function getIcon(path: string, hasShadow = false) {
    const img = await getAsset(path, 'svg');
    const container = new Container();
    const sprite = Sprite.from(img);
    sprite.anchor.set(0.5);
    const size = sprite.bounds.maxX * 2;

    if (hasShadow) {
        const shadow = Sprite.from(img);
        shadow.anchor.set(0.5);
        container.addChild(shadow);
        shadow.tint = 0x000000;
        shadow.alpha = 0.25;
        shadow.position.set(0, 1);
        const size = shadow.bounds.maxX;

        shadow.width = size;
        shadow.height = size;
    }

    sprite.width = size / 2;
    sprite.height = size / 2;
    container.scale.set(1);

    container.addChild(sprite);
    return container;
}
let assetLoaded = false;
const assetLoadQueue = [] as (() => void)[];
function onAssetsLoadComplete() {
    assetLoaded = true;
    assetLoadQueue.forEach((cb) => {
        cb();
    });
}

export function onAssetsLoad(cb: () => void) {
    if (assetLoaded) cb();
    else {
        assetLoadQueue.push(cb);
    }
}
export function webpFallback(fallback = 'png') {
    return supportWebP ? 'webp' : fallback;
}
export function isSupportWebP(cb: (support: boolean) => void) {
    var kTestImages = {
        lossy: 'UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA',
    };
    var img = new Image();
    img.onload = function () {
        supportWebP = img.width > 0 && img.height > 0;
        cb(supportWebP);
    };
    img.onerror = function () {
        supportWebP = false;
        cb(supportWebP);
    };
    img.src = 'data:image/webp;base64,' + kTestImages.lossy;
}

function convertImgToCanvasSource(img: HTMLImageElement): Texture {
    return Texture.from(img);

    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get canvas context');

    ctx.drawImage(img, 0, 0);

    const sc = new CanvasSource(canvas);
    return Texture.from(sc);
}
